import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Bed, Plus, User, DoorOpen, ChevronDown, ChevronUp, UserPlus } from 'lucide-react';

export default function RoomBedManager({ api, houseId, canManage, isStaff, clients, onResidentAssigned }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [expandedRoom, setExpandedRoom] = useState(null);
  const [assignOpen, setAssignOpen] = useState(null);
  const [roomForm, setRoomForm] = useState({ room_label: '', room_type: 'standard', bed_count: 2, amenities: '' });
  const [assignForm, setAssignForm] = useState({ user_id: '', move_in_date: '', program_phase: 'orientation', rent_amount: 0 });

  const loadRooms = useCallback(() => {
    setLoading(true);
    api.get(`/housing/houses/${houseId}/rooms`)
      .then(r => setRooms(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, houseId]);

  useEffect(() => { loadRooms(); }, [loadRooms]);

  const addRoom = async () => {
    if (!roomForm.room_label) return toast.error('Room label is required');
    try {
      await api.post(`/housing/houses/${houseId}/rooms`, {
        ...roomForm,
        amenities: roomForm.amenities ? roomForm.amenities.split(',').map(a => a.trim()).filter(Boolean) : []
      });
      toast.success('Room added!');
      setAddOpen(false);
      setRoomForm({ room_label: '', room_type: 'standard', bed_count: 2, amenities: '' });
      loadRooms();
      if (onResidentAssigned) onResidentAssigned();
    } catch {
      toast.error('Failed to add room');
    }
  };

  const assignBed = async (roomId, bedId) => {
    if (!assignForm.user_id) return toast.error('Select a participant');
    try {
      await api.post('/housing/residents/assign-bed', {
        user_id: assignForm.user_id,
        room_id: roomId,
        bed_id: bedId,
        move_in_date: assignForm.move_in_date || undefined,
        program_phase: assignForm.program_phase,
        rent_amount: assignForm.rent_amount
      });
      toast.success('Resident assigned to bed!');
      setAssignOpen(null);
      setAssignForm({ user_id: '', move_in_date: '', program_phase: 'orientation', rent_amount: 0 });
      loadRooms();
      if (onResidentAssigned) onResidentAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
  };

  const totalBeds = rooms.reduce((sum, r) => sum + (r.beds?.length || 0), 0);
  const occupiedBeds = rooms.reduce((sum, r) => sum + (r.beds?.filter(b => b.occupancy_status === 'occupied').length || 0), 0);
  const availableBeds = totalBeds - occupiedBeds;

  if (loading) return <div className="text-xs text-muted-foreground py-4 text-center">Loading rooms...</div>;

  return (
    <Card className="border-0 shadow-sm rounded-xl" data-testid="room-bed-manager">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <DoorOpen size={14} /> Rooms & Beds
          <Badge variant="outline" className="ml-2 text-[10px]">{rooms.length} rooms</Badge>
          <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">{availableBeds} available</Badge>
          <Badge className="bg-amber-50 text-amber-700 border-0 text-[10px]">{occupiedBeds} occupied</Badge>
        </CardTitle>
        {canManage && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" data-testid="add-room-btn">
                <Plus size={12} /> Add Room
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Add Room</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label>Room Label</Label>
                  <Input value={roomForm.room_label} onChange={e => setRoomForm({...roomForm, room_label: e.target.value})} placeholder="e.g., Room 1, Suite A" data-testid="room-label-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Room Type</Label>
                    <Select value={roomForm.room_type} onValueChange={v => setRoomForm({...roomForm, room_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="suite">Suite</SelectItem>
                        <SelectItem value="shared">Shared</SelectItem>
                        <SelectItem value="single">Single</SelectItem>
                        <SelectItem value="dormitory">Dormitory</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Number of Beds</Label>
                    <Input type="number" min={1} max={12} value={roomForm.bed_count} onChange={e => setRoomForm({...roomForm, bed_count: parseInt(e.target.value) || 1})} data-testid="bed-count-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Amenities (comma-separated)</Label>
                  <Input value={roomForm.amenities} onChange={e => setRoomForm({...roomForm, amenities: e.target.value})} placeholder="e.g., AC, Closet, Desk" />
                </div>
                <Button onClick={addRoom} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-add-room">Add Room</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </CardHeader>
      <CardContent>
        {rooms.length > 0 ? (
          <div className="space-y-2">
            {rooms.map(room => {
              const occupied = room.beds?.filter(b => b.occupancy_status === 'occupied').length || 0;
              const available = (room.beds?.length || 0) - occupied;
              const isExpanded = expandedRoom === room.room_id;

              return (
                <div key={room.room_id} className="rounded-xl border border-slate-100 overflow-hidden" data-testid={`room-${room.room_id}`}>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                    onClick={() => setExpandedRoom(isExpanded ? null : room.room_id)}
                    data-testid={`room-toggle-${room.room_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                        <DoorOpen size={16} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{room.room_label}</p>
                        <p className="text-xs text-muted-foreground">{room.room_type} - {room.bed_count} bed{room.bed_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {available > 0 && <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">{available} open</Badge>}
                      {occupied > 0 && <Badge className="bg-amber-50 text-amber-700 border-0 text-[10px]">{occupied} filled</Badge>}
                      {room.amenities?.length > 0 && <Badge variant="outline" className="text-[10px] hidden sm:inline-flex">{room.amenities.join(', ')}</Badge>}
                      {isExpanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="p-3 space-y-2 bg-white">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {(room.beds || []).map(bed => (
                          <div
                            key={bed.bed_id}
                            className={`p-3 rounded-xl border-2 text-center transition-all ${
                              bed.occupancy_status === 'occupied'
                                ? 'border-amber-200 bg-amber-50'
                                : bed.occupancy_status === 'maintenance'
                                ? 'border-slate-300 bg-slate-100'
                                : 'border-emerald-200 bg-emerald-50 hover:border-emerald-400'
                            }`}
                            data-testid={`bed-${bed.bed_id}`}
                          >
                            <Bed size={20} className={`mx-auto mb-1 ${
                              bed.occupancy_status === 'occupied' ? 'text-amber-600'
                              : bed.occupancy_status === 'maintenance' ? 'text-slate-400'
                              : 'text-emerald-600'
                            }`} />
                            <p className="text-xs font-medium">{bed.bed_label}</p>
                            <Badge className={`mt-1 text-[9px] ${
                              bed.occupancy_status === 'occupied' ? 'bg-amber-100 text-amber-800 border-0'
                              : bed.occupancy_status === 'maintenance' ? 'bg-slate-200 text-slate-600 border-0'
                              : 'bg-emerald-100 text-emerald-700 border-0'
                            }`}>
                              {bed.occupancy_status}
                            </Badge>
                            {bed.occupancy_status === 'occupied' && bed.current_resident_id && (
                              <p className="text-[10px] text-amber-700 mt-1 flex items-center justify-center gap-0.5">
                                <User size={9} /> Assigned
                              </p>
                            )}
                            {bed.occupancy_status === 'available' && isStaff && (
                              <Dialog open={assignOpen === bed.bed_id} onOpenChange={(open) => setAssignOpen(open ? bed.bed_id : null)}>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="ghost" className="mt-1 h-6 text-[10px] text-emerald-700 hover:bg-emerald-100 gap-0.5 px-2" data-testid={`assign-bed-${bed.bed_id}`}>
                                    <UserPlus size={10} /> Assign
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="rounded-2xl">
                                  <DialogHeader><DialogTitle>Assign {bed.bed_label} in {room.room_label}</DialogTitle></DialogHeader>
                                  <div className="space-y-3 pt-2">
                                    <div className="space-y-1.5">
                                      <Label>Participant</Label>
                                      <Select value={assignForm.user_id} onValueChange={v => setAssignForm({...assignForm, user_id: v})}>
                                        <SelectTrigger data-testid="assign-participant-select"><SelectValue placeholder="Select participant" /></SelectTrigger>
                                        <SelectContent>{(clients || []).map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                                      </Select>
                                    </div>
                                    <div className="space-y-1.5"><Label>Move-in Date</Label><Input type="date" value={assignForm.move_in_date} onChange={e => setAssignForm({...assignForm, move_in_date: e.target.value})} /></div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="space-y-1.5">
                                        <Label>Program Phase</Label>
                                        <Select value={assignForm.program_phase} onValueChange={v => setAssignForm({...assignForm, program_phase: v})}>
                                          <SelectTrigger><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="orientation">Orientation</SelectItem>
                                            <SelectItem value="stabilization">Stabilization</SelectItem>
                                            <SelectItem value="recovery">Recovery</SelectItem>
                                            <SelectItem value="leadership">Leadership</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1.5">
                                        <Label>Rent Amount ($)</Label>
                                        <Input type="number" min={0} value={assignForm.rent_amount} onChange={e => setAssignForm({...assignForm, rent_amount: parseFloat(e.target.value) || 0})} />
                                      </div>
                                    </div>
                                    <Button onClick={() => assignBed(room.room_id, bed.bed_id)} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="submit-assign-bed">
                                      Assign to {bed.bed_label}
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-xl">
            <DoorOpen size={24} className="mx-auto mb-2 text-slate-300" />
            <p className="text-xs">{canManage ? 'No rooms yet — add rooms to manage individual beds' : 'No rooms configured'}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
